import React, { useEffect, useState } from 'react';
import useIsMobile from '../hooks/useIsMobile';

const emptyLine = { item_id: '', requested_qty: '', remarks: '' };

function FoodStoreIssue({ token, selectedProperty }) {
  const { isMobile } = useIsMobile();
  const [stores, setStores] = useState([]);
  const [items, setItems] = useState([]);
  const [rows, setRows] = useState([]);
  const [selectedReq, setSelectedReq] = useState(null);
  const [issueLines, setIssueLines] = useState([]);
  const [error, setError] = useState('');
  const [message, setMessage] = useState('');
  const [form, setForm] = useState({ store_id: '', department_name: '', remarks: '', lines: [{ ...emptyLine }] });

  const chainId = String(selectedProperty?.chain_id || '').trim();
  const propertyId = String(selectedProperty?.id || '').trim();
  const headers = (extra = {}) => ({ Authorization: `Bearer ${token}`, 'x-chain-id': chainId, 'x-property-id': propertyId, ...extra });

  const parseResponse = async (res) => {
    const raw = await res.text();
    try {
      const json = raw ? JSON.parse(raw) : {};
      return { data: json, message: json?.error || '' };
    } catch {
      return { data: {}, message: raw || '' };
    }
  };

  const loadBase = async () => {
    if (!chainId || !propertyId) return;
    try {
      const [storeRes, itemRes, reqRes] = await Promise.all([
        fetch('/api/v1/food/masters/stores', { headers: headers() }),
        fetch('/api/v1/food/masters/items', { headers: headers() }),
        fetch('/api/v1/food/store-requisitions', { headers: headers() })
      ]);
      const store = await parseResponse(storeRes);
      const item = await parseResponse(itemRes);
      const req = await parseResponse(reqRes);
      if (!storeRes.ok) return setError(store.message || 'Failed to load stores');
      if (!itemRes.ok) return setError(item.message || 'Failed to load items');
      if (!reqRes.ok) return setError(req.message || 'Failed to load requisitions');
      setStores(Array.isArray(store.data) ? store.data : []);
      setItems(Array.isArray(item.data) ? item.data : []);
      setRows(Array.isArray(req.data) ? req.data : []);
    } catch {
      setError('Network error while loading store issue module');
    }
  };

  useEffect(() => {
    if (!chainId || !propertyId) {
      setError('Select a property first from Hotel Config');
      return;
    }
    setError('');
    setMessage('');
    loadBase();
  }, [token, chainId, propertyId]);

  const setLine = (idx, patch) => {
    setForm((prev) => ({ ...prev, lines: prev.lines.map((l, i) => (i === idx ? { ...l, ...patch } : l)) }));
  };

  const addLine = () => setForm((prev) => ({ ...prev, lines: [...prev.lines, { ...emptyLine }] }));
  const removeLine = (idx) => setForm((prev) => ({ ...prev, lines: prev.lines.filter((_, i) => i !== idx) }));

  const createRequisition = async () => {
    setError('');
    setMessage('');
    if (!form.store_id) return setError('Select store');
    const lines = form.lines
      .map((l) => ({ item_id: l.item_id, requested_qty: Number(l.requested_qty || 0), remarks: l.remarks || null }))
      .filter((l) => l.item_id && l.requested_qty > 0);
    if (!lines.length) return setError('Add at least one line with quantity');
    try {
      const res = await fetch('/api/v1/food/store-requisitions', {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({
          store_id: form.store_id,
          department_name: form.department_name || null,
          remarks: form.remarks || null,
          lines
        })
      });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Create requisition failed');
      setMessage(`Requisition created: ${data.id}`);
      setForm({ store_id: '', department_name: '', remarks: '', lines: [{ ...emptyLine }] });
      loadBase();
    } catch {
      setError('Network error while creating requisition');
    }
  };

  const loadReqDetail = async (id) => {
    setError('');
    setMessage('');
    try {
      const res = await fetch(`/api/v1/food/store-requisitions/${id}`, { headers: headers() });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Failed to load requisition detail');
      setSelectedReq(data);
      setIssueLines((Array.isArray(data.items) ? data.items : []).map((i) => ({ requisition_item_id: i.id, pending_qty: Number(i.pending_qty || 0), issued_qty: Number(i.pending_qty || 0) > 0 ? String(i.pending_qty) : '0' })));
    } catch {
      setError('Network error while loading requisition');
    }
  };

  const setIssueLine = (idx, value) => setIssueLines((prev) => prev.map((l, i) => (i === idx ? { ...l, issued_qty: value } : l)));

  const approveIssue = async () => {
    if (!selectedReq?.id) return;
    setError('');
    setMessage('');
    const lines = issueLines
      .map((l) => ({ requisition_item_id: l.requisition_item_id, issued_qty: Number(l.issued_qty || 0) }))
      .filter((l) => l.issued_qty > 0);
    if (!lines.length) return setError('Enter issue quantity for at least one pending line');
    try {
      const res = await fetch(`/api/v1/food/store-requisitions/${selectedReq.id}/approve-issue`, {
        method: 'POST',
        headers: headers({ 'Content-Type': 'application/json' }),
        body: JSON.stringify({ lines })
      });
      const { data, message: msg } = await parseResponse(res);
      if (!res.ok) return setError(msg || 'Issue failed');
      setMessage(`Issue voucher generated: #${data.issue_no} (${data.status})`);
      setSelectedReq(null);
      setIssueLines([]);
      loadBase();
    } catch {
      setError('Network error while issuing');
    }
  };

  const inputStyle = { padding: '0.5rem', border: '1px solid #dcdde1', borderRadius: '6px' };
  const btnBase = { padding: '0.5rem 0.85rem', border: 'none', borderRadius: '6px', cursor: 'pointer' };

  return (
    <div className="page-shell">
      <h2 style={{ color: '#2f855a' }}>Food Management - Store Requisition & Issue</h2>
      {error && <div className="error-banner">{error}</div>}
      {message && <div className="success-banner">{message}</div>}

      <div className="filter-bar">
        <h3>Create Kitchen Requisition</h3>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(220px, 1fr))', gap: '0.6rem', marginBottom: '0.6rem' }}>
          <select value={form.store_id} onChange={(e) => setForm({ ...form, store_id: e.target.value })} style={inputStyle}>
            <option value="">Select Store</option>
            {stores.map((s) => <option key={s.id} value={s.id}>{s.store_name}</option>)}
          </select>
          <input value={form.department_name} onChange={(e) => setForm({ ...form, department_name: e.target.value })} style={inputStyle} placeholder="Department (Kitchen/Outlet)" />
          <input value={form.remarks} onChange={(e) => setForm({ ...form, remarks: e.target.value })} style={inputStyle} placeholder="Remarks" />
        </div>
        {form.lines.map((l, idx) => (
          <div key={idx} style={{ display: 'grid', gridTemplateColumns: isMobile ? 'repeat(2, minmax(0, 1fr))' : '2fr 1fr 2fr auto', gap: '0.5rem', marginBottom: '0.4rem' }}>
            <select value={l.item_id} onChange={(e) => setLine(idx, { item_id: e.target.value })} style={inputStyle}>
              <option value="">Select Item</option>
              {items.map((i) => <option key={i.id} value={i.id}>{`${i.item_code} - ${i.item_name}`}</option>)}
            </select>
            <input type="number" min="0.001" step="0.001" value={l.requested_qty} onChange={(e) => setLine(idx, { requested_qty: e.target.value })} style={inputStyle} placeholder="Qty" />
            <input value={l.remarks} onChange={(e) => setLine(idx, { remarks: e.target.value })} style={inputStyle} placeholder="Line remarks" />
            <button onClick={() => removeLine(idx)} className="btn-danger" style={btnBase}>Remove</button>
          </div>
        ))}
        <div style={{ display: 'flex', gap: '0.5rem' }}>
          <button onClick={addLine} className="btn-primary" style={btnBase}>Add Line</button>
          <button onClick={createRequisition} className="btn-success" style={btnBase}>Create Requisition</button>
        </div>
      </div>

      <div className="filter-bar">
        <h3>Requisition List</h3>
        {rows.length === 0 ? <p>No requisitions found</p> : (
          <table style={{ width: '100%', borderCollapse: 'collapse', background: '#fff' }}>
            <thead>
              <tr style={{ background: '#2f855a', color: '#fff' }}>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Store</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Department</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Requested Qty</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Issued Qty</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Status</th>
                <th style={{ padding: '0.5rem', textAlign: 'left' }}>Action</th>
              </tr>
            </thead>
            <tbody>
              {rows.map((r) => (
                <tr key={r.id} style={{ borderBottom: '1px solid var(--theme-border)' }}>
                  <td style={{ padding: '0.5rem' }}>{r.store_name}</td>
                  <td style={{ padding: '0.5rem' }}>{r.department_name || '-'}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.requested_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{Number(r.issued_qty || 0).toFixed(3)}</td>
                  <td style={{ padding: '0.5rem' }}>{r.status}</td>
                  <td style={{ padding: '0.5rem' }}>
                    {(r.status === 'PENDING_APPROVAL' || r.status === 'PARTIAL_ISSUED') && (
                      <button onClick={() => loadReqDetail(r.id)} className="btn-primary" style={btnBase}>Approve & Issue</button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      {selectedReq && (
        <div className="filter-bar">
          <h3>Approve & Issue - {selectedReq.department_name || selectedReq.requested_by || ''}</h3>
          {(selectedReq.items || []).map((i, idx) => (
            <div key={i.id} style={{ display: 'grid', gridTemplateColumns: isMobile ? '1fr' : '2fr 1fr 1fr', gap: '0.6rem', marginBottom: '0.5rem' }}>
              <div>{`${i.item_code || ''} - ${i.item_name || ''}`}</div>
              <div>{`Pending: ${Number(i.pending_qty || 0).toFixed(3)}`}</div>
              <input type="number" min="0" step="0.001" value={issueLines[idx]?.issued_qty || '0'} onChange={(e) => setIssueLine(idx, e.target.value)} style={inputStyle} />
            </div>
          ))}
          <div style={{ display: 'flex', gap: '0.5rem' }}>
            <button onClick={approveIssue} className="btn-success" style={btnBase}>Generate Issue Voucher</button>
            <button onClick={() => { setSelectedReq(null); setIssueLines([]); }} className="btn-muted" style={btnBase}>Close</button>
          </div>
        </div>
      )}
    </div>
  );
}

export default FoodStoreIssue;
